import { FetchResult } from "@apollo/client/link/core/types";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";

import client from "@/client/apollo";
import { RootState } from "@/data/store";
import { trackNetworkRequest } from "@/data/task/actions";
import {
  CreateTaskBlockDocument,
  CreateTaskBlockInput,
  CreateTaskBlockMutation,
  CreateTaskBlockMutationVariables,
  QuestionType,
  TaskBlockFragment,
  TaskBlockFragmentDoc,
  UpdateTaskBlockDocument,
  UpdateTaskBlockMutation,
  UpdateTaskBlockMutationVariables,
  UpdateTaskBlocksDocument,
  UpdateTaskBlocksMutation,
  UpdateTaskBlocksMutationVariables,
} from "@/generated/graphql";

import { clearQuestionFormError } from "../question-form-validation";
import { UpdatedBlock } from "./types";
import {
  getDescendantBlocks,
  getTempId,
  getUpdatedBlocks,
  removeTempId,
} from "./utils";

interface AddTaskPayload extends CreateTaskBlockInput {
  taskId: string;
  questionType: QuestionType;
}

interface DeleteTaskPayload {
  id: string;
}

interface UpdateHiddenPayload {
  id: string;
  hidden: boolean;
}

export const addTaskBlock = createAsyncThunk<
  FetchResult<CreateTaskBlockMutation>,
  AddTaskPayload,
  { state: RootState }
>("taskLayout/addTaskBlock", async (payload, thunkAPI) => {
  const { taskId, questionType, parentNodeId, shuffle, points, hidden } =
    payload;
  const taskBlockId = getTempId();
  const questionId = getTempId();
  const nodeId = uuid();

  const sortedBlocks = thunkAPI.getState().taskLayout.sortedTaskBlocks;
  let previousNodeId: string | null = null;
  // get previousNodeId for the inserted block
  const previousIndex = sortedBlocks
    .map((t) => t.parentNodeId)
    .lastIndexOf(parentNodeId ?? null);
  if (previousIndex === -1 || questionType === QuestionType.Overview) {
    previousNodeId = null;
  } else {
    const block = sortedBlocks[previousIndex]!;
    if (block.questionType === QuestionType.Overview) {
      previousNodeId = null;
    } else if (parentNodeId !== null) {
      previousNodeId = block.nodeId;
    }
  }
  const mutationPromise = client.mutate<
    CreateTaskBlockMutation,
    CreateTaskBlockMutationVariables
  >({
    mutation: CreateTaskBlockDocument,
    variables: {
      taskId,
      input: {
        questionType,
        nodeId,
        parentNodeId,
        previousNodeId,
        shuffle,
        hidden,
      },
    },
    optimisticResponse: () => {
      const block = {
        id: taskBlockId,
        nodeId,
        previousNodeId: previousNodeId,
        parentNodeId: parentNodeId ?? null,
        quantity: 1,
        hidden: false,
        deleted: false,
        shuffle: shuffle ?? false,
        points: points ?? 0,
        order: null,
        orderLabel: null,
        question: {
          id: questionId,
          cacheId: `${questionId}:task`,
          questionType,
          shortPrompt: null,
          body: null,
          updatedAt: new Date().toISOString(),
        },
        questionSelection: null,
        libraryQuestionId: null,
        libraryQuestionVersionId: null,
        markerRubric: null,
        questionSets: null,
        socraticQuestionerEnabled: false,
        updatedAt: new Date().toISOString(),
      };
      thunkAPI.dispatch(add({ blocks: [block] }));
      return {
        createTaskBlock: {
          __typename: "TaskBlock",
          ...block,
          question: {
            __typename: "Question",
            ...block.question,
          },
        },
      };
    },
    update: (cache, { data }) => {
      if (!data?.createTaskBlock) return;
      if (data.createTaskBlock.id !== taskBlockId) {
        thunkAPI.dispatch(
          updateTempTaskBlock({ id: taskBlockId, block: data.createTaskBlock })
        );
        removeTempId(questionId);
        removeTempId(taskBlockId);
        cache.modify({
          id: `Task:${taskId}`,
          fields: {
            blocks(existingBlocks = []) {
              const newBlockRef = cache.writeFragment({
                data: data.createTaskBlock,
                fragment: TaskBlockFragmentDoc,
                fragmentName: "TaskBlock",
              });
              return [...existingBlocks, newBlockRef];
            },
          },
        });
      }
    },
  });
  thunkAPI.dispatch(trackNetworkRequest({ requestPromise: mutationPromise }));
  return mutationPromise;
});

export const remove = createAsyncThunk<
  unknown,
  DeleteTaskPayload,
  { state: RootState }
>("taskLayout/remove", async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const { sortedTaskBlocks: sortedQuestions } = state.taskLayout;
  const block = sortedQuestions.find((t) => t.id === payload.id);
  if (block) {
    const updatedBlocks = getUpdatedBlocks(sortedQuestions, {
      id: block.id,
      nodeId: block.nodeId,
      parentNodeId: block.parentNodeId,
      previousNodeId: block.previousNodeId,
      deleted: true,
    });
    thunkAPI.dispatch(updateTaskBlocks(updatedBlocks));
    if (block.questionId) {
      thunkAPI.dispatch(clearQuestionFormError({ blockId: block.id }));
    }

    return await client.mutate<
      UpdateTaskBlocksMutation,
      UpdateTaskBlocksMutationVariables
    >({
      mutation: UpdateTaskBlocksDocument,
      variables: {
        taskBlocksList: [
          ...updatedBlocks,
          ...getDescendantBlocks(sortedQuestions, block.nodeId).map((t) => ({
            id: t.id,
            nodeId: t.nodeId,
            parentNodeId: t.parentNodeId,
            previousNodeId: t.previousNodeId,
            deleted: true,
          })),
        ],
      },
    });
  }
});

interface MovePayload {
  nodeId: string;
  parentNodeId: string | null;
  previousNodeId: string | null;
}

export const moveBlocks = createAsyncThunk<
  unknown,
  MovePayload,
  { state: RootState }
>("taskLayout/move", async (payload, thunkAPI) => {
  const { nodeId, parentNodeId, previousNodeId } = payload;
  const state = thunkAPI.getState();
  const originalSortedBlocks = state.taskLayout.sortedTaskBlocks;
  const node = originalSortedBlocks.find((block) => block.nodeId === nodeId);
  if (!node) return;
  const updatedBlocks = getUpdatedBlocks(originalSortedBlocks, {
    id: node.id,
    nodeId,
    previousNodeId,
    parentNodeId,
    deleted: false,
  });
  if (updatedBlocks.length === 0) return;
  thunkAPI.dispatch(updateTaskBlocks(updatedBlocks));
  return client.mutate<
    UpdateTaskBlocksMutation,
    UpdateTaskBlocksMutationVariables
  >({
    mutation: UpdateTaskBlocksDocument,
    variables: {
      taskBlocksList: updatedBlocks,
    },
  });
});

export const updateHiddenOverview = createAsyncThunk<
  unknown,
  UpdateHiddenPayload,
  { state: RootState }
>("taskLayout/updateHiddenOverview", async (payload, thunkAPI) => {
  const { id, hidden } = payload;
  thunkAPI.dispatch(updateHiddenTaskBlock(payload));
  return await client.mutate<
    UpdateTaskBlockMutation,
    UpdateTaskBlockMutationVariables
  >({
    mutation: UpdateTaskBlockDocument,
    variables: {
      taskBlockId: id,
      input: {
        hidden,
      },
    },
  });
});

export const add = createAction<{ blocks: TaskBlockFragment[] }>(
  "taskLayout/add"
);

export const updateTempTaskBlock = createAction<{
  id: string;
  block: TaskBlockFragment;
}>("taskLayout/updateTempTaskBlock");

export const updateTaskBlocks = createAction<Array<UpdatedBlock>>(
  "taskLayout/updateTaskBlocks"
);

export const updateHiddenTaskBlock = createAction<UpdateHiddenPayload>(
  "taskLayout/updateTaskBlockHidden"
);
